import { useState } from "react";
import { useIntl } from "react-intl";
import { Box, Button, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import toast from "react-hot-toast";
import Modal from "../Unknown/Modal";
import CustomPhoneInput from "../UI/PhoneInput";
import translate from "../../i18n/translate";
import { StaticOtpItem, userApi } from "../../modules/services/userService";
import { generateOtpCode, isValidOtpPhone } from "../../common/otp";

type Props = {
  // Edit mode when set: the phone is fixed and only the code changes
  item: StaticOtpItem | null;
  onClose: () => void;
};

const OTP_LENGTH = 6;

// Mounted only while open, so the initial state is the reset
const StaticOtpModal = ({ item, onClose }: Props) => {
  const intl = useIntl();
  const isEdit = !!item;

  const [phone, setPhone] = useState("+972");
  const [code, setCode] = useState(() => item?.code ?? generateOtpCode());
  const [isPhoneInvalid, setIsPhoneInvalid] = useState(false);
  const [isCodeInvalid, setIsCodeInvalid] = useState(false);

  const [sendOtp, { isLoading }] = userApi.useSendOtpMutation();

  const onPhoneChange = (value: string) => {
    setPhone(value);
    setIsPhoneInvalid(false);
  };

  const onCodeChange = (value: string) => {
    setCode(value.replace(/\D/g, "").slice(0, OTP_LENGTH));
    setIsCodeInvalid(false);
  };

  const onGenerateCode = () => {
    setCode(generateOtpCode());
    setIsCodeInvalid(false);
  };

  const onSubmit = async () => {
    const phoneDigits = (item?.phone ?? phone).replace(/\D/g, "");
    const isPhoneValid = isEdit || isValidOtpPhone(phoneDigits);
    const isCodeValid = code.length === OTP_LENGTH;

    setIsPhoneInvalid(!isPhoneValid);
    setIsCodeInvalid(!isCodeValid);
    if (!isPhoneValid || !isCodeValid) return;

    try {
      await sendOtp({ phone: phoneDigits, otp: code, isStatic: true }).unwrap();
      navigator.clipboard.writeText(code);
      toast.success(translate(isEdit ? "staticOtpUpdateSuccess" : "otpSetStaticSuccess"));
      onClose();
    } catch {
      toast.error(translate("otpSetFailed"));
    }
  };

  return (
    <Modal
      title={translate(isEdit ? "staticOtpEdit" : "staticOtpAdd")}
      visible
      style={{ maxWidth: "500px" }}
      setVisible={onClose}
    >
      <Box my={2} p={2} display="flex" flexDirection="column" alignItems="center" width={1} gap={2}>
        {isEdit ? (
          <Typography variant="h5">{item.phone}</Typography>
        ) : (
          <Box width={1}>
            <CustomPhoneInput phone={phone} setPhone={onPhoneChange} />
            {isPhoneInvalid && (
              <Typography variant="body2" color="error" mt={1}>
                {translate("otpPhoneInvalid")}
              </Typography>
            )}
          </Box>
        )}
        <TextField
          fullWidth
          color="warning"
          label={intl.formatMessage({ id: "staticOtpCodeLabel" })}
          value={code}
          onChange={(e) => onCodeChange(e.target.value)}
          error={isCodeInvalid}
          helperText={isCodeInvalid ? intl.formatMessage({ id: "staticOtpCodeInvalid" }) : undefined}
          inputProps={{ inputMode: "numeric", maxLength: OTP_LENGTH }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={intl.formatMessage({ id: "staticOtpGenerateCode" })}>
                  <IconButton color="warning" onClick={onGenerateCode}>
                    <AutorenewIcon />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{ "& input": { fontSize: "32px", fontFamily: "monospace", letterSpacing: "8px", textAlign: "center" } }}
        />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {translate("otpIsStaticHint")}
        </Typography>
        <Button size="large" variant="contained" color="warning" onClick={onSubmit} disabled={isLoading}>
          {translate(isEdit ? "staticOtpSave" : "staticOtpAdd")}
        </Button>
      </Box>
    </Modal>
  );
};

export default StaticOtpModal;
